import { AuthFetchWithResponse } from 'src/Types/api'
import { ApiResult, throwApiError } from 'src/Libs/api'
import { StatusCodes } from '@e3dc-react/shell/Libs'
import { AuthFetchError } from '@e3dc-react/shell/Types'

export interface EmcConfigDto {
  id: number
  deviceLinkId: number
  useCT: boolean
  ratioCT: string
  modbusEnabled?: boolean
  modbusProtocol?: 'e3dc' | 'enerfin' | 'sunspec'
  modbusPort?: number
  emcIpAddress?: string | null
  ipAddress?: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string
}

export const getEmcConfig = async (
  installationId: number,
  deviceId: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<Nullable<EmcConfigDto> | null>> => {
  try {
    const response = await authFetch<Nullable<EmcConfigDto>>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceId}/config/emc`,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<Nullable<EmcConfigDto>>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to get EMC config:')
  }
}

export type EcmConfigPUTDto = Pick<EmcConfigDto, 'modbusEnabled' | 'modbusProtocol' | 'modbusPort'> & {
  useCT: boolean | null
  ratioCT: string | null
}

export const updateEmcConfig = async (
  installationId: number,
  deviceId: string,
  emcConfig: EcmConfigPUTDto,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceId}/config/emc`,
      method: 'PUT',
      payload: emcConfig,
    })

    if (![StatusCodes.OK, StatusCodes.NO_CONTENT].includes(response.status)) {
      return new ApiResult<null, Error | AuthFetchError>(null, true, new Error(`Unexpected status code ${response.status} when updating EMC config`))
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to update EMC config:')
  }
}

export interface StorageConfigDto {
  id: number
  deviceLinkId: number
  gridDisconnector: boolean
  sgrUsedPower: number
  pvInverterStringCount: number
  pvInverterCountryStandard: string
  emergencyPowerCapacityReserve: number
  installedPVCapacity: number
  agreedConnectionLoad: number
  powerReductionFrom: number
  powerReduction: 'Pv Leistung' | 'Anschlusswirkleistung'
  reactivePowerSpecification: boolean
  reactivePowerSpecificationValue: number
  reactivePowerSpecificationVariant: 'induktiv' | 'Kapazitiv'
  frequencyReverseProtectionLower: number
  frequencyReverseProtectionUpper: number
  votageProtectionAvg: number
  voltageFuseProtection: number
  voltageDropProtection: number
  emsOperatingRangeAuto: boolean //Advanced Settings - Battery operation range - auto
  emsMaxChargePower: null | number // Advanced Settings - Battery operation range - max charge
  emsMaxDischargePower: null | number // Advanced Settings - Battery operation range - max discharge
  emsDischargeStartPower: null | number // Advanced Settings - Battery operation range - threshold
  emsPowersaveEnabled: null | boolean // Advanced Settings - PV Inverter-Controller Settings
  powerLimitsUsed: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string
}

export const getStorageConfig = async (
  installationId: number,
  deviceId: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<Nullable<StorageConfigDto> | null>> => {
  try {
    const response = await authFetch<Nullable<StorageConfigDto>>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceId}/config/storage`,
    })

    // no config has been created for this storage yet, callers can treat a `null` value as such
    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<null>(null)
    }

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(
        null,
        true,
        new Error(`Unexpected status code ${response.status} when fetching storage config`)
      )
    }

    return new ApiResult<Nullable<StorageConfigDto>>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to get storage config:')
  }
}

export type StorageConfigPUTDto = Pick<
  StorageConfigDto,
  | 'pvInverterStringCount'
  | 'emergencyPowerCapacityReserve'
  | 'emsOperatingRangeAuto'
  | 'emsMaxChargePower'
  | 'emsMaxDischargePower'
  | 'emsDischargeStartPower'
  | 'emsPowersaveEnabled'
  | 'powerLimitsUsed'
  | 'frequencyReverseProtectionLower'
> &
  Partial<Pick<StorageConfigDto, 'sgrUsedPower'>> & { name: string }

export const updateStorageConfig = async (
  installationId: number,
  deviceId: string,
  storageConfig: Partial<StorageConfigPUTDto>,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceId}/config/storage`,
      method: 'PUT',
      payload: storageConfig,
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.NO_CONTENT) {
      return new ApiResult<null, Error | AuthFetchError>(
        null,
        true,
        new Error(`Unexpected status code ${response.status} when updating storage config`)
      )
    }

    return new ApiResult<null>(null)
  } catch (error) {
    throw new Error('Error updating storage config: ' + (error as Error)?.message)
  }
}
